import localforage from 'localforage';

class DataService {

  constructor() {
    localforage.config({
      driver: localforage.INDEXEDDB,
      name: "miAplicacionDB",
      storeName: "misDatos",
      description: "Almacena datos de ejemplo para mi aplicación React",
    });
  }

  /**
   * Carga todos los datos asociados a una clave específica desde IndexedDB.
   * @param {string} key La clave bajo la cual se almacenan los datos.
   * @returns {Promise<Array>} Una promesa que se resuelve con un array de datos.
   * Si no hay datos o hay un error, resuelve con un array vacío.
   */
  async loadAllData(key) { // Marca la función como 'async'
    let datos = [];
    try {
      // Usa 'await' para esperar a que la Promesa de localforage.getItem se resuelva
      const value = await localforage.getItem(key);

      // Asegúrate de que el valor sea un array antes de establecerlo
      if (Array.isArray(value)) {
        datos = value;
      } else if (value === null) {
        // Si no hay datos para esa clave, devuelve un array vacío
        datos = [];
      } else {
        // Si el valor no es un array y no es null, podría ser un objeto o string simple,
        // decide cómo quieres manejarlo. Aquí lo convertimos en un array con el valor.
        console.warn(`El valor para la clave "${key}" no es un array. Se ha recuperado:`, value);
        datos = [value]; // O [] si prefieres ignorar valores no-array
      }
    } catch (err) {
      console.error(`Error al cargar los datos para la clave "${key}":`, err);
      // En caso de error, es mejor devolver un array vacío para evitar que la aplicación falle
      datos = [];
    }
    return datos;
  }

  /**
   * Guarda un array de datos bajo una clave específica en IndexedDB.
   * Sobrescribirá cualquier dato existente con la misma clave.
   * @param {string} key La clave bajo la cual se almacenarán los datos.
   * @param {Array} data El array de datos a guardar.
   * @returns {Promise<any>} Una promesa que se resuelve cuando los datos han sido guardados.
   */
  async saveData(key, data) {
    if (!Array.isArray(data)) {
      console.error("La función saveData espera un array como segundo argumento.");
      return Promise.reject(new Error("Los datos a guardar deben ser un array."));
    }
    try {
      await localforage.setItem(key, data);
      console.log(`Datos guardados exitosamente para la clave "${key}".`);
    } catch (err) {
      console.error(`Error al guardar los datos para la clave "${key}":`, err);
      throw err; // Relanza el error para que el llamador pueda manejarlo
    }
  }

  /**
   * Elimina los datos asociados a una clave específica de IndexedDB.
   * @param {string} key La clave de los datos a eliminar.
   * @returns {Promise<void>} Una promesa que se resuelve cuando los datos han sido eliminados.
   */
  async removeData(key) {
    try {
      await localforage.removeItem(key);
      console.log(`Datos eliminados exitosamente para la clave "${key}".`);
    } catch (err) {
      console.error(`Error al eliminar los datos para la clave "${key}":`, err);
      throw err;
    }
  }

  /**
   * Vacía todo el almacén de datos de IndexedDB para esta instancia de localforage.
   * ¡Úsalo con precaución!
   * @returns {Promise<void>} Una promesa que se resuelve cuando el almacén ha sido vaciado.
   */
  async clearAllData() {
    try {
      await localforage.clear();
      console.log("Todos los datos de la base de datos han sido eliminados.");
    } catch (err) {
      console.error("Error al vaciar la base de datos:", err);
      throw err;
    }
  }

  /**
   * Obtiene el número de elementos en el almacén de datos.
   * @returns {Promise<number>} Una promesa que se resuelve con el número de elementos.
   */
  async countItems() {
    try {
      const count = await localforage.length();
      console.log(`Número de elementos en el almacén: ${count}`);
      return count;
    } catch (err) {
      console.error("Error al contar los elementos:", err);
      throw err;
    }
  }
}

export default new DataService(); // Exporta una instancia única de tu servicio
